/**
 * ═══════════════════════════════════════════════════════════
 * Furniture Pricing Engine — 제작가구 자동 견적 엔진
 * ═══════════════════════════════════════════════════════════
 *
 * 프로젝트 A 견적서 (93건, 2.249억) 기반 가격 모델
 * 카테고리별 겉넓이(㎡) 단가 적용
 *
 * 파이프라인:
 *   도면(DXF/PDF) → 가구 블록 추출 → 사이즈 파싱 →
 *   카테고리 분류 → 단가 적용 → 견적서 생성
 */

import costTaxonomy from '@/config/cost_taxonomy.json';

// ══════════════════════════════════════════════
// 1. 가격 모델 (프로젝트 A 실데이터 기반)
// ══════════════════════════════════════════════

export interface PriceModel {
  category: string;
  label: string;
  count: number;        // 참조 건수
  totalAmount: number;  // 총액
  pricePerM2: {
    min: number;
    avg: number;
    high: number;
    max: number;
  };
  description: string;
}

/**
 * 실데이터 기반 단가 모델 (원/㎡ — 겉넓이 기준)
 * 
 * 데이터 소스 (26건 실 견적서):
 *   - 김해도서관 제작가구 견적서 (라이브러리엔, 2025.01)
 *   - 00교육지원청 제작가구 견적서 (2025.07)
 *   - 남원하늘중학교 인테리어 제작가구 (2026.02)
 *   - 프로젝트 A 인테리어 제작가구 (2026.03)
 * 
 * 산출 방법: 겉넓이(㎡) = 2×(W×D + W×H + D×H) / 1,000,000
 *           단가 = 총 공급가 ÷ 겉넓이
 */
export const PRICE_MODELS: PriceModel[] = [
  {
    category: 'bookshelf_wall',
    label: '벽면서가',
    count: 53, // 4개 프로젝트 합산
    totalAmount: 165_600_000,
    pricePerM2: { min: 80_000, avg: 136_000, high: 158_000, max: 180_000 },
    // 실데이터 범위: 79,866~176,252 (11건 평균 136,150)
    description: '높이 1600~3300mm 벽면 부착형 서가. 깊이 180~700mm. LPM/PB 마감.',
  },
  {
    category: 'bookshelf_display',
    label: '전시용서가',
    count: 19,
    totalAmount: 45_740_000,
    pricePerM2: { min: 108_000, avg: 154_000, high: 195_000, max: 235_000 },
    // ★ FU-04 실제 제출가 역산 기준 (정면 W×H 투영면적 기준, 6면적 아님)
    // A:107,859 B:226,677 C:125,881 D:234,594 E:112,905 F:119,136 → avg≈154,500
    // 깊이 150mm 얇은 오픈형 — 6면 표면적 기준 아닌 정면투영면적(W×H)으로 산출
    description: '깊이 150mm 내외 전시 전용 서가. 매거진/신간 디스플레이. 정면투영면적(W×H) 기준 단가.',
  },
  {
    category: 'desk',
    label: '데스크/카운터',
    count: 9,
    totalAmount: 24_120_000,
    pricePerM2: { min: 106_000, avg: 201_000, high: 293_000, max: 385_000 },
    // 실데이터 범위: 106,111~384,265 (5건 평균 200,780) — 안내데스크, 카운터, 사서데스크
    description: '안내데스크, 업무데스크, 카운터. 높이 850~1000mm. 배선홀, 수납 포함.',
  },
  {
    category: 'table',
    label: '열람테이블',
    count: 13,
    totalAmount: 10_530_000,
    pricePerM2: { min: 63_000, avg: 96_000, high: 147_000, max: 197_000 },
    description: '열람/학습 테이블. 높이 750mm, 다양한 사이즈.',
  },
  {
    category: 'cabinet',
    label: '수납장/하부장',
    count: 5,
    totalAmount: 11_470_000,
    pricePerM2: { min: 197_000, avg: 243_000, high: 267_000, max: 290_000 },
    // 실데이터: 하부장 242,528/㎡ (1건) + 기존 데이터
    description: '벽면 수납장, 하부장. 높이 850~2700mm. 도어 포함.',
  },
  {
    category: 'reading_nook',
    label: '리딩누크/휴게공간',
    count: 4,
    totalAmount: 7_970_000,
    pricePerM2: { min: 110_000, avg: 160_000, high: 175_000, max: 190_000 },
    description: '휴게형 독서공간. 다양한 마감재 혼합.',
  },
  {
    category: 'sofa_bench',
    label: '붙박이의자/소파',
    count: 9,
    totalAmount: 21_280_000,
    pricePerM2: { min: 113_000, avg: 170_000, high: 192_000, max: 213_000 },
    // 실데이터 범위: 112,546~212,798 (3건 평균 169,957) — 쿠션/패브릭 포함
    description: '붙박이의자, 스툴, 소파. 인조가죽/패브릭 쿠션, 하단 수납 구조.',
  },
  {
    category: 'louver',
    label: '그리드루버',
    count: 5,
    totalAmount: 6_100_000,
    pricePerM2: { min: 144_000, avg: 196_000, high: 214_000, max: 232_000 },
    description: '그리드 루버 파티션. 깊이 150mm.',
  },
  {
    category: 'planter',
    label: '플랜트박스',
    count: 6,
    totalAmount: 4_340_000,
    pricePerM2: { min: 170_000, avg: 222_000, high: 248_000, max: 274_000 },
    // 실데이터 범위: 169,671~273,913 (2건 평균 221,792) — 스텐리스 포함 시 상한
    description: '식물 배치용 박스. 스텐리스/PB. 방수 처리.',
  },
  {
    category: 'cube_shelf',
    label: '큐브서가',
    count: 5,
    totalAmount: 14_460_000,
    pricePerM2: { min: 80_000, avg: 143_000, high: 172_000, max: 200_000 },
    // 실데이터: 큐브서가 142,792/㎡ (1건)
    description: '입체형 큐브 서가. 자유 배치. 깊이 400~630mm.',
  },
  {
    category: 'special',
    label: '특수가구',
    count: 4,
    totalAmount: 10_350_000,
    pricePerM2: { min: 134_000, avg: 185_000, high: 211_000, max: 236_000 },
    // 실데이터: 133,802~235,693 (2건 평균 184,748) — 세면대, 싱크대, 분리수거 등
    description: '세면대, 기저귀교환대, 싱크대, 분리수거함 등 특수 가구. 배관/위생기구 포함.',
  },
  {
    category: 'moving_wall',
    label: '무빙월',
    count: 4,
    totalAmount: 0,
    pricePerM2: { min: 200_000, avg: 350_000, high: 425_000, max: 500_000 },
    description: '이동식 파티션. 배면 거울 옵션.',
  },
];

/**
 * 함양도서관 입찰제출용 PDF(공급가액 231,740,000원) 기준 보정계수.
 *
 * 기본 ㎡ 단가 모델은 여러 프로젝트 평균값이라 도면만 입력했을 때
 * 제출 견적 레벨보다 낮거나 높은 카테고리가 생긴다. 아래 계수는
 * 동일 95개 품목을 기준으로 카테고리별 산출 합계가 입찰제출 PDF의
 * 금액 수준에 수렴하도록 보정한다.
 */
export const BID_LEVEL_CATEGORY_MULTIPLIER: Record<string, number> = {
  desk: 0.7552,
  planter: 0.9865,
  bookshelf_wall: 1.3519,
  bookshelf_display: 0.9286,
  louver: 1.4933,
  sofa_bench: 1.1717,
  reading_nook: 1.4920,
  cabinet: 1.3619,
  cube_shelf: 0.5581,
  table: 1.1983,
};

export const CATEGORY_IMAGES: Record<string, string> = {
  'bookshelf_wall': 'https://images.unsplash.com/photo-1594620302200-9a7620473ce2?q=80&w=600&auto=format&fit=crop',
  'bookshelf_display': 'https://images.unsplash.com/photo-1544457070-4cd773b4d71e?q=80&w=600&auto=format&fit=crop',
  'desk': 'https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd?q=80&w=600&auto=format&fit=crop',
  'table': 'https://images.unsplash.com/photo-1533090161767-e6ffed986c88?q=80&w=600&auto=format&fit=crop',
  'cabinet': 'https://images.unsplash.com/photo-1595515106969-1ce29566ff1c?q=80&w=600&auto=format&fit=crop',
  'reading_nook': 'https://images.unsplash.com/photo-1490312278390-ab64016e0aa9?q=80&w=600&auto=format&fit=crop',
  'sofa_bench': 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?q=80&w=600&auto=format&fit=crop',
  'louver': 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=600&auto=format&fit=crop',
  'planter': 'https://images.unsplash.com/photo-1485955900006-10f4d324d411?q=80&w=600&auto=format&fit=crop',
  'cube_shelf': 'https://images.unsplash.com/photo-1595514535311-6677fdd1ffab?q=80&w=600&auto=format&fit=crop',
  'moving_wall': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=600&auto=format&fit=crop',
  'special': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=80&w=600&auto=format&fit=crop',
  'default': 'https://images.unsplash.com/photo-1538688525198-9b88f6f53126?q=80&w=600&auto=format&fit=crop'
};

export function getCategoryImageUrl(category: string, name: string = ''): string {
  if (category === 'sofa_bench') {
    if (name.includes('소파')) {
      return 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?q=80&w=600&auto=format&fit=crop'; // Modern Sofa
    }
    // 붙박이의자, 벤치 등
    return 'https://images.unsplash.com/photo-1581428982868-e410dd047a90?q=80&w=600&auto=format&fit=crop'; // Built-in bench / Window seat
  }
  return CATEGORY_IMAGES[category] || CATEGORY_IMAGES['default'];
}

// ══════════════════════════════════════════════
// 2. 가구 항목 타입 (상세 원가 포함)
// ══════════════════════════════════════════════

/** 재질 분류 */
export type MaterialType =
  | 'MDF_PB'      // MDF/PB 합판 (레거시 폴백용)
  | 'LPM'         // PB 심재 + LPM 멜라민 함침
  | 'PB'          // 일반 PB 나출
  | 'MDF_PAINT'   // MDF + 도장 마감 (우레탄 등)
  | 'MDF_FILM'    // MDF + 인테리어 필름 마감
  | 'plywood'     // 합판 (자작 등)
  | 'oak_wood'    // 오크 집성판
  | 'lauan_wood'  // 라왕 집성판
  | 'solid_wood'  // 일반 원목/집성재
  | 'metal'       // 금속 (스틸/알루미늄)
  | 'glass'       // 유리
  | 'stone'       // 석재 (대리석/인조석)
  | 'fabric'      // 패브릭/가죽
  | 'mixed';      // 복합재질

/** 재질별 가격 배율 (MDF 기준 1.0) */
export const MATERIAL_MULTIPLIER: Record<MaterialType, number> = {
  MDF_PB: 1.0,
  PB: 0.85,
  LPM: 1.0,
  MDF_PAINT: 1.4,
  MDF_FILM: 1.25,
  plywood: 1.15,
  oak_wood: 1.85,
  lauan_wood: 1.35,
  solid_wood: 1.8,
  metal: 1.4,
  glass: 1.3,
  stone: 2.0,
  fabric: 0.9,
  mixed: 1.2,
};

/**
 * 가구용 자재 시중단가 (원/㎡)
 * [출처] 한국물가정보(주) 2026년 1월호 — 건설재료 시중단가표
 * ※ 판재 1장(1,220×2,440mm = 2.978㎡) 기준 시중단가 → ㎡ 환산
 * ※ 부자재(접착제·피스·마감 필름) 포함 실부착 단가
 */
export const MATERIAL_UNIT_PRICE: Record<MaterialType, {
  label: string;
  spec: string;
  unitPricePerM2: number;    // 원/㎡ (18T 기준 부자재 포함)
  thicknessPrices?: Record<number, number>; // 두께별 조사 단가 (원/㎡)
  pricePerSheet?: number;    // 원/장 (실구매 참고가)
  edgeMaterial: string;
  source: string;            // 출처 (한국물가정보 페이지)
}> = {
  MDF_PB: {
    label: 'MDF (LPM 마감)',
    spec: 'MDF 일반 + LPM 양면마감 + ABS엣지',
    unitPricePerM2: 11_500,
    thicknessPrices: { 15: 10_000, 18: 11_500, 24: 15_500, 30: 19_000 },
    pricePerSheet: 20_000,   // MDF 18T 1장(1220×2440) 시중가
    edgeMaterial: 'ABS 엣지 1mm',
    source: '한국물가정보 2026.01 p.328 (목재·합판류)',
  },
  PB: {
    label: 'PB (파티클보드)',
    spec: '내장용 파티클보드 노출',
    unitPricePerM2: 8_500,
    thicknessPrices: { 15: 7_500, 18: 8_500, 24: 12_500, 30: 15_000 },
    edgeMaterial: '무가래 가공',
    source: '한국물가정보 2026.01 p.328 (목재·합판류)',
  },
  LPM: {
    label: 'LPM 보드',
    spec: '저압 멜라민 함침지 양면 부착 (PB코어)',
    unitPricePerM2: 12_500,
    thicknessPrices: { 15: 11_000, 18: 12_500, 24: 16_500, 30: 21_000 },
    edgeMaterial: 'ABS 엣지 1mm',
    source: '한국물가정보 2026.01 p.328 (목재·합판류)',
  },
  MDF_PAINT: {
    label: 'MDF (우레탄도장)',
    spec: '고밀도 MDF 자재비 기준 (도장비 별도 합산)',
    unitPricePerM2: 11_500,
    thicknessPrices: { 15: 10_000, 18: 11_500, 24: 15_500, 30: 19_000 },
    edgeMaterial: '도장면 일체형 마감',
    source: '한국물가정보 2026.01 p.329 / 자사 도장비 적용',
  },
  MDF_FILM: {
    label: 'MDF (인테리어필름)',
    spec: '일반 MDF 자재비 기준 (시공비 별도 합산)',
    unitPricePerM2: 11_500,
    thicknessPrices: { 15: 10_000, 18: 11_500, 24: 15_500, 30: 19_000 },
    edgeMaterial: '필름 래핑 마감',
    source: '한국물가정보 2026.01 인테리어필름 단가 반영',
  },
  plywood: {
    label: '자작합판',
    spec: '핀란드산 자작합판, 표면 도장/오일 마감',
    unitPricePerM2: 24_500,
    thicknessPrices: { 15: 20_500, 18: 24_500, 24: 33_000, 30: 41_500 },
    pricePerSheet: 73_000,   // 자작합판 18T 1장 시중가
    edgeMaterial: '무가래 엣지 (도장)',
    source: '한국물가정보 2026.01 p.330 (합판·특수목)',
  },
  oak_wood: {
    label: '오크 집성판',
    spec: '하드우드 오크 집성재 (오일/바니쉬 마감)',
    unitPricePerM2: 43_600,
    thicknessPrices: { 15: 38_000, 18: 43_600, 24: 55_000, 30: 68_000 },
    edgeMaterial: '동일원목 노출/연마/코팅',
    source: '한국물가정보 2026.01 - 오크(참나무) 집성목 시중가(13만원/장) 기준',
  },
  lauan_wood: {
    label: '라왕 집성판',
    spec: '솔리드 라왕 집성재 (스테인/오일 마감)',
    unitPricePerM2: 30_200,
    thicknessPrices: { 15: 26_000, 18: 30_200, 24: 39_000, 30: 48_000 },
    edgeMaterial: '동일원목 노출/연마/코팅',
    source: '한국물가정보 2026.01 - 라왕(나왕) 집성목 시중가(9만원/장) 기준',
  },
  solid_wood: {
    label: '소나무 집성재',
    spec: '국내산 소나무 집성각재 패널',
    unitPricePerM2: 48_000,
    thicknessPrices: { 15: 40_000, 18: 48_000, 24: 64_500, 30: 81_000 },
    edgeMaterial: '동일원목 엣지',
    source: '한국물가정보 2026.01 p.332 (집성재·원목)',
  },
  metal: {
    label: '냉연강판 (분체도장)',
    spec: 'KS 냉연강판 + 에폭시 분체도장',
    unitPricePerM2: 35_000,
    thicknessPrices: { 15: 35_000, 18: 35_000, 24: 35_000, 30: 35_000 },
    edgeMaterial: '패턴 다드미 / 용접 마감',
    source: '한국물가정보 2026.01 p.198 (철강재류)',
  },
  glass: {
    label: '강화유리',
    spec: '강화유리 (투명 또는 반투명)',
    unitPricePerM2: 43_000,
    edgeMaterial: '연마 · 폴리싱 처리',
    source: '한국물가정보 2026.01 p.284 (유리류)',
  },
  stone: {
    label: '인조대리석 / 포세린',
    spec: '포세린 타일 또는 수입 인조대리석',
    unitPricePerM2: 88_000,
    edgeMaterial: '연마 가공 (평면/사면)',
    source: '한국물가정보 2026.01 p.268 (석재·타일류)',
  },
  fabric: {
    label: '가구용 패브릭 원단',
    spec: 'A급 커팅·접착, 시공 포함',
    unitPricePerM2: 19_000,
    edgeMaterial: '스테칭 마감',
    source: '한국물가정보 2026.01 p.356 (섬유·직물류)',
  },
  mixed: {
    label: '복합재질 (MDF+철재+유리)',
    spec: 'MDF + 철제 보강 + 유리 혼합 구성',
    unitPricePerM2: 28_000,
    thicknessPrices: { 15: 25_000, 18: 28_000, 24: 35_000, 30: 42_000 },
    edgeMaterial: '혼합 엣지 (재질별 상이)',
    source: '한국물가정보 2026.01 — MDF·철재 평균 적용',
  },
};

/**
 * 책장/선반 단수 AI 자동 추천
 * 높이 H 기준 300~320mm 간격으로 1단 구성
 */
export function recommendShelfCount(h: number, name: string): number {
  const n = name.toLowerCase();
  const isShelf = n.includes('서가') || n.includes('책장') || n.includes('벽장') ||
                  n.includes('선반') || n.includes('랙') || n.includes('shelf') ||
                  n.includes('bookcase') || n.includes('wr') || n.includes('수납');
  if (!isShelf) return 0; // 0 = 해당없음
  // 350mm 간격으로 1단, 최소 1단 최대 10단 (Math.floor 사용)
  return Math.max(1, Math.min(10, Math.floor((h - 200) / 350)));
}

/** 부대비용 비율 (총원가 100% 분배 기준 모델) */
export const COST_RATIOS = {
  // 3. 자재비 (30%)
  rawMaterial: 0.20,      // 원자재
  cutting: 0.05,          // 재단
  edge: 0.05,             // 엣지처리
  
  // 4. 공장제작비 (30%)
  assembly: 0.13,         // 조립비용
  painting: 0.05,         // 도장
  outsourcing: 0.10,      // 외주비 (대리석,유리,조명등)
  packaging: 0.02,        // 포장비용

  // 5. 일반관리비 (5%) - 통상 제조/자재비의 일정%이나 전체대비 비율로 환산
  generalMgmt: 0.05,

  // 6. 물류/설치비 (12%)
  transport: 0.05,        // 운송비
  loading: 0.02,          // 상하차비
  settingLabor: 0.05,     // 가구세팅 인건비

  // 7. 기타/이윤 (11%)
  miscExpense: 0.05,      // 공과잡비/경비
  profit: 0.06,           // 기업이윤
};

/** 상세 원가 내역 */
export interface CostBreakdown {
  material: MaterialType;       // 재질
  materialLabel: string;        // 재질 한글
  materialMultiplier: number;   // 재질 배율
  basePrice: number;            // 겉넓이 기반 기준 단가
  
  // 3. 자재비
  unitPricePerM2?: number;     // 자재 시중단가 (원/㎡, 한국물가정보)
  materialSourcePage?: string; // 한국물가정보 페이지
  totalMaterialAreaM2?: number;// 계산된 정미면적
  lossMultiplier?: number;     // 적용된 로스 할증
  rawMaterialCost: number;     // 원자재
  cuttingCost: number;         // 재단
  edgeCost: number;            // 엣지처리
  materialTotal: number;       // 자재비 소계
  
  // 4. 공장제작비 및 옵션
  assemblyLaborDays?: number;  // 가구조립공 품수 (인/일)
  assemblyLaborRate?: number;  // 가구조립공 단가
  assemblyCost: number;        // 조립비용
  paintingLaborDays?: number;  // 도장공 품수
  paintingLaborRate?: number;  // 도장공 단가
  paintingCost: number;        // 도장
  outsourcingCost: number;     // 외주비
  optionsCost: number;         // 하드웨어/콘센트 옵션 등 추가
  packagingCost: number;       // 포장비용
  manufacturingTotal: number;  // 공장제작비 소계
  
  // 5. 일반관리비
  generalMgmtCost: number;     // 일반관리비 8%
  
  // 6. 물류/설치비
  transportCost: number;       // 운송비
  loadingCost: number;         // 상하차비
  settingLaborDays?: number;   // 보통인부 품수
  settingLaborRate?: number;   // 보통인부 단가
  settingLaborCost: number;    // 가구세팅 인건비
  installTotal: number;        // 설치비 소계
  
  // 7. 기타
  miscExpenseCost: number;     // 공과잡비/경비
  profitCost: number;          // 기업이윤
  
  appliedRates?: {
    generalMgmt: number;
    miscExpense: number;
    profit: number;
  };

  // 8. 합계
  totalOriginCost: number;     // 총원가
  naraBidCost: number;         // 조달청 87% 보정가
  netCost: number;             // 최종 단가
  autoParts?: { partName: string; material: string; w?: number; d?: number; h?: number; calculation: string }[];
  moduleBreakdown?: { label?: string; width_mm: number }[];
}

export interface FurnitureItem {
  no: number;
  name: string;         // 제품명
  code: string;         // FU-XX 코드
  location: string;     // 설치 공간
  w: number;            // 폭 (mm)
  d: number;            // 깊이 (mm)
  h: number;            // 높이 (mm)
  qty: number;          // 수량
  thickness?: number;
  hasHardware?: boolean; // 하드웨어 옵션
  hasSocket?: boolean;   // 콘센트 옵션
  hasLighting?: boolean; // 간접조명 LED 옵션
  hasCushion?: boolean;  // 인조가죽 쿠션 옵션
  category: string;     // 자동 분류
  surfaceM2: number;    // 겉넓이 (㎡)
  volumeM3: number;     // 체적 (㎥)
  unitPrice: number;    // 단가 (원)
  totalPrice: number;   // 금액 (원)
  priceGrade: 'min' | 'avg' | 'high' | 'max';
  basis: string;        // 산출 근거
  costBreakdown: CostBreakdown;  // 상세 원가
  calcParts?: { partName: string; material: string; w?: number; d?: number; h?: number; calculation: string }[]; // 파츠별 분해 산출근거
  moduleBreakdown?: { label?: string; width_mm: number }[]; // 베이(Bay) 명시적 분할 규격
  marketComparison?: {
    naraPrice: number;
    naraUrl: string;
    fursysPrice: number;
    livartPrice: number;
    koaPrice: number;
  };
}

export interface FurnitureEstimate {
  projectName: string;
  generatedAt: string;
  estimateId?: string;
  recipient?: string;
  totalItems: number;
  totalQty: number;
  subtotal: number;     // 공급가액
  vat: number;          // 부가세
  grandTotal: number;   // 합계 (원가)
  discount: number;     // 단수조정(에누리)
  finalGrandTotal: number; // 최종 청구 합계
  items: FurnitureItem[];
  categoryBreakdown: {
    category: string;
    label: string;
    itemCount: number;
    totalQty: number;
    totalAmount: number;
  }[];
  costSummary: {        // 원가 구성 요약
    totalRawMaterial: number;
    totalCutting: number;
    totalEdge: number;
    totalAssembly: number;
    totalPainting: number;
    totalOutsourcing: number;
    totalOptions: number;
    totalPackaging: number;
    totalGeneralMgmt: number;
    totalTransport: number;
    totalLoading: number;
    totalSettingLabor: number;
    totalMiscExpense: number;
    totalProfit: number;
    
    // Aggregate aliases
    totalMaterial: number;
    totalManufacturing: number;
    totalLogisticsInstall: number;
  };
  priceGrade: 'min' | 'avg' | 'high' | 'max';
  estimateType: 'designinsight' | 'narajangteo' | 'compare_3'; // 견적서 종류
  warnings: string[];
  unitCount?: number;   // 세대수 (아파트 등)
}

/** 재질 한글명 */
export const MATERIAL_LABELS: Record<MaterialType, string> = {
  MDF_PB: 'MDF/PB 합판',
  LPM: 'LPM 멜라민',
  PB: 'PB 나출',
  MDF_PAINT: 'MDF 도장',
  MDF_FILM: 'MDF 필름',
  plywood: '합판',
  oak_wood: '오크 집성판',
  lauan_wood: '라왕 집성판',
  solid_wood: '원목',
  metal: '금속(스틸)',
  glass: '유리',
  stone: '석재',
  fabric: '패브릭',
  mixed: '복합재질',
};

/** 제품명에서 재질 자동 추정 */
export function estimateMaterial(name: string, category: string): MaterialType {
  const n = name.toLowerCase();
  if (n.includes('원목') || n.includes('solid')) return 'solid_wood';
  if (n.includes('스틸') || n.includes('금속') || n.includes('metal') || n.includes('철제')) return 'metal';
  if (n.includes('유리') || n.includes('glass') || n.includes('거울')) return 'glass';
  if (n.includes('대리석') || n.includes('석재') || n.includes('stone')) return 'stone';
  if (n.includes('패브릭') || n.includes('가죽') || n.includes('소파') || n.includes('의자')) return 'fabric';
  if (n.includes('합판') || n.includes('plywood')) return 'plywood';

  // 카테고리별 기본 재질
  if (category === 'sofa_bench' || category === 'reading_nook') return 'fabric';
  if (category === 'louver' || category === 'moving_wall') return 'mixed';

  return 'MDF_PB';
}

/** 상세 원가 계산 */
export function calcCostBreakdown(
  basePrice: number,
  material: MaterialType,
  estimateType: string = 'designinsight',
  customRatios?: Partial<typeof COST_RATIOS>,
  customMultipliers?: Partial<typeof MATERIAL_MULTIPLIER>,
  options?: { hasHardware?: boolean; hasSocket?: boolean; hasLighting?: boolean; hasCushion?: boolean; thickness?: number; shelfCount?: number; w?: number; d?: number; h?: number; category?: string; contractMode?: 'standalone' | 'included' }
): CostBreakdown {
  let mult = (customMultipliers && customMultipliers[material]) ?? MATERIAL_MULTIPLIER[material];
  if (options?.thickness && options.thickness !== 18) {
    // 두께 증가에 따른 체적 증가분 반영 (18T 기준 대비)
    mult *= (options.thickness / 18);
  }
  // 100% 기준 총원가 도출
  const originCostRaw = basePrice * mult;
  const originCost = Math.round(originCostRaw / 1000) * 1000;

  // 적용할 Ratio 구성 (기본값 병합)
  const ratios = { ...COST_RATIOS, ...(customRatios || {}) };

  // 비율에 따라 세부 항목 분배 (천원 단위)
  const calc = (ratio: number) => Math.round((originCost * ratio) / 1000) * 1000;

  // ── 자재비·재단비·엣지비 정밀 산출 (패널별 실측 기반) ──────────────────────
  let rawMaterialCost: number;
  let cuttingCost = 0;
  let edgeCost = 0;
  // 패널별 상세 내역 (PDF 상세표와 동일한 로직)
  let panelDetails: Array<{name: string; wMm: number; dMm: number; thickMm: number; qty: number; unitPrice?: number; cost?: number}> = [];

  // 시중단가 테이블 (원/㎡) 수동 지정 단가 우선 적용 (15T, 18T, 24T, 30T)
  const reqThick = options?.thickness ?? 18;
  const materialData = MATERIAL_UNIT_PRICE[material];
  let unitPricePerM2 = materialData.unitPricePerM2; // 18T 기본값
  if (materialData.thicknessPrices && materialData.thicknessPrices[reqThick]) {
    unitPricePerM2 = materialData.thicknessPrices[reqThick];
  } else {
    // 테이블에 없는 특수 두께일 경우 비례 할증 폴백
    const thicknessMultiplier = reqThick / 18;
    unitPricePerM2 = Math.floor(unitPricePerM2 * thicknessMultiplier);
  }

  const category = options?.category;

  if (options?.w && options?.d && options?.h) {
    const W = options.w;   // mm
    const D = options.d;
    const H = options.h;
    const T = options.thickness ?? 18; // 기본 판재 두께 (mm) - 사용자 지정 또는 AI 추천값 적용
    const Tb = 9;          // 뒷판 두께 (mm)
    const shelfCount = options.shelfCount ?? 0;

    // 패널 목록 정의 (바닥/천장/측판/뒷판/선반)
    if (category === 'sofa_bench') {
      // 붙박이의자 (소파/스툴) 전용 명세
      const hasBackrest = H > 500;
      const backH = hasBackrest ? H - 400 : 0; // 좌판 높이(약 400) 제외한 등받이 높이
      panelDetails = [
        { name: '하부골조',    wMm: W,       dMm: D,           thickMm: T,  qty: 1 },
        { name: '하부수납(합판)',wMm: W - T*2, dMm: D - 50,    thickMm: 15, qty: 1 }, // 서랍/개폐 구조
        { name: '좌판(MDF)',   wMm: W,       dMm: D,           thickMm: 15, qty: 1 },
        ...(hasBackrest ? [{ name: '등판(MDF)', wMm: W, dMm: backH, thickMm: 15, qty: 1 }] : []),
        { name: '고탄력스폰지', wMm: W,       dMm: D + (hasBackrest ? backH : 0), thickMm: 50, qty: 1 },
        { name: '인조가죽원단', wMm: W,       dMm: D + (hasBackrest ? backH : 0), thickMm: 2,  qty: 1 },
      ];
    } else if (category === 'bookshelf_display' || category === 'display_bookshelf') {
      // 전시용서가 특화 (전면 거치형 빗각 선반 및 전면 립 구조)
      panelDetails = [
        { name: '바닥판',    wMm: W,       dMm: D,           thickMm: T,  qty: 1 },
        { name: '천장판',    wMm: W,       dMm: D,           thickMm: T,  qty: 1 },
        { name: '측판(좌)',  wMm: D,       dMm: H - T * 2,   thickMm: T,  qty: 1 },
        { name: '측판(우)',  wMm: D,       dMm: H - T * 2,   thickMm: T,  qty: 1 },
        { name: '뒷판(보강)',wMm: W - T*2, dMm: H - T * 2,   thickMm: 15, qty: 1 },
        ...(shelfCount > 0 ? [
          { name: '전시선반',   wMm: W - T*2, dMm: D + 40,  thickMm: T, qty: shelfCount },
          { name: '거치턱(Lip)',wMm: W - T*2, dMm: 40,      thickMm: T, qty: shelfCount }
        ] : []),
      ];
    } else if (category === 'desk' || category === 'info_desk' || category === 'table') {
      let bayCount = W > 1200 ? Math.ceil(W / 900) : 1;
      let bayW = Math.round(W / bayCount);
      panelDetails = [
        { name: '상판(Top)',       wMm: bayW,    dMm: D,           thickMm: 24, qty: bayCount },
        { name: '측판/다리(Leg)',  wMm: D - 50,  dMm: H - 24,      thickMm: 24, qty: bayCount + 1 },
        { name: '가림판(Modesty)', wMm: bayW,    dMm: Math.min(H/2, 400), thickMm: 18, qty: bayCount },
        { name: '하부보강/수납',   wMm: bayW,    dMm: D/2,         thickMm: 18, qty: bayCount * 2 },
      ];
    } else {
      // 일반 가구 (수납장, 책장 등) 패널 명세 - 대형 규격은 Bay 분할 가동
      let bayCount = W > 1200 ? Math.ceil(W / 900) : 1;
      let bayW = Math.round(W / bayCount);
      panelDetails = [
        { name: '바닥판',      wMm: bayW,       dMm: D,           thickMm: T,  qty: bayCount },
        { name: '천장판',      wMm: bayW,       dMm: D,           thickMm: T,  qty: bayCount },
        { name: '측판(외부)',   wMm: D,       dMm: H - T * 2,   thickMm: T,  qty: 2 },
        ...(bayCount > 1 ? [{ name: '수직칸막이', wMm: D, dMm: H - T * 2, thickMm: T, qty: bayCount - 1 }] : []),
        { name: '뒷판',        wMm: bayW - T*2, dMm: H - T * 2,   thickMm: Tb, qty: bayCount },
        ...(shelfCount > 0 ? [{ name: '중간선반', wMm: bayW - T*2, dMm: D - T, thickMm: T, qty: shelfCount * bayCount }] : []),
      ];
    }

    // 재단비 단가: MDF 2,500원/m, 자작 3,200원/m, 원목 4,500원/m, 금속 3,800원/m 등
    const cuttingRatePerM: Record<MaterialType, number> = {
      MDF_PB: 2_500, PB: 2_200, LPM: 2_500, MDF_PAINT: 2_500, MDF_FILM: 2_500,
      plywood: 3_200, oak_wood: 4_000, lauan_wood: 3_800, solid_wood: 4_500, metal: 3_800,
      glass: 5_500, stone: 6_000, fabric: 1_200, mixed: 3_000,
    };
    // 엣지비 단가: ABS 1,800원/m, 무가래 2,200원/m, 원목 3,500원/m 등
    const edgeRatePerM: Record<MaterialType, number> = {
      MDF_PB: 1_800, PB: 1_200, LPM: 1_800, MDF_PAINT: 0 /* 도장처리 */, MDF_FILM: 0 /* 래핑 */,
      plywood: 2_200, oak_wood: 3_500, lauan_wood: 3_500, solid_wood: 3_500, metal: 0,
      glass: 2_800, stone: 3_200, fabric: 0, mixed: 2_000,
    };

    // 패널별 면적 합산 → 단위 단가 분기 처리 후 원자재/가공비 합산
    // 패널별 면적 합산 → 단위 단가 분기 처리 후 원자재/가공비 합산
    rawMaterialCost = 0;
    cuttingCost = 0;
    edgeCost = 0;
    
    let totalArea = 0;
    const isWood = ['MDF_PB', 'PB', 'LPM', 'MDF_PAINT', 'MDF_FILM', 'plywood', 'oak_wood', 'lauan_wood', 'solid_wood'].includes(material);
    const lossMultiplierRatio = isWood ? 1.18 : 1.10;

    for (const p of panelDetails) {
      const aM2 = (p.wMm / 1000) * (p.dMm / 1000) * p.qty;
      const perimM = 2 * ((p.wMm / 1000) + (p.dMm / 1000)) * p.qty;
      
      let reqThick = p.thickMm;
      let pUnitPrice = unitPricePerM2; // 기본값
      if (materialData.thicknessPrices && materialData.thicknessPrices[reqThick]) {
        pUnitPrice = materialData.thicknessPrices[reqThick];
      } else {
        pUnitPrice = Math.floor(materialData.unitPricePerM2 * (reqThick / 18));
      }
      
      let pCutRate = cuttingRatePerM[material] ?? 2500;
      let pEdgeRate = edgeRatePerM[material] ?? 1800;

      // 쿠션/원단 등 이형 자재 단가 분기 (sofa_bench 전용)
      if (p.name === '고탄력스폰지') { pUnitPrice = 15_000; pCutRate = 800; pEdgeRate = 0; }
      else if (p.name === '인조가죽원단') { pUnitPrice = 24_000; pCutRate = 1_200; pEdgeRate = 0; }
      else if (p.name.includes('MDF')) { pUnitPrice = 11_500; pCutRate = 2_500; pEdgeRate = 0; }
      
      totalArea += aM2;
      const rawCost = Math.floor(aM2 * lossMultiplierRatio * pUnitPrice);
      rawMaterialCost += rawCost;
      cuttingCost += Math.floor(perimM * pCutRate);
      edgeCost += Math.floor(perimM * 0.45 * pEdgeRate);
      
      p.unitPrice = pUnitPrice;
      p.cost = rawCost;
    }

  } else {
    // 치수 미입력 시 비율 기반 폴백 (단위 환산만)
    rawMaterialCost = originCost * ratios.rawMaterial;
    cuttingCost = originCost * ratios.cutting;
    edgeCost = originCost * ratios.edge;
  }

  const materialTotal = rawMaterialCost + cuttingCost + edgeCost;


  let assemblyLaborDays, assemblyLaborRate, assemblyCost;
  let paintingLaborDays, paintingLaborRate, paintingCost;
  let settingLaborDays, settingLaborRate, settingLaborCost;

  if (options?.w && options?.d && options?.h) {
    const wm = Math.max(options.w / 1000, 0.1);
    const dm = Math.max(options.d / 1000, 0.1);
    const hm = Math.max(options.h / 1000, 0.1);
    const volumeM3 = wm * dm * hm;
    const surfaceM2 = 2 * (wm * dm + wm * hm + dm * hm);
    
    // 카테고리별 조립 난이도 (기본=1.0)
    let difficulty = 1.0;
    if (category === 'desk' || category === 'info_desk') difficulty = 2.5;
    else if (category === 'bookshelf_display') difficulty = 1.8;
    else if (category === 'sofa_bench' || category === 'moving_wall') difficulty = 1.5;
    else if (category === 'planter' || category === 'reading_nook') difficulty = 2.0;

    // 조립 인건비 (내장가구공/판넬조립공 2026.1.1 기준 / costTaxonomy 반영)
    // - 사용자 요청: 현장 시공단가가 너무 낮아 조립투입 요율을 상향 (0.22) 및 카테고리별 난이도 적용
    assemblyLaborRate = (costTaxonomy as any).labor_rates_krw?.['판넬조립공'] || 243121;
    assemblyLaborDays = Math.max(0.05, Math.round(((volumeM3 * 0.22) + (surfaceM2 * 0.022)) * difficulty * 100) / 100);
    assemblyCost = Math.round(assemblyLaborDays * assemblyLaborRate); // 원 단위 리얼 단가

    // 도장/필름 인건비 (도장공/내소재공 2026.1.1 기준 / costTaxonomy 반영)
    // 원자재가 자작합판, 원목, 금속(분체도장), 도장전용MDF 등인 경우 적용. PB, LPM 등은 0.
    if (material === 'PB' || material === 'LPM' || material === 'MDF_PB' || material === 'glass' || material === 'stone') {
      paintingLaborRate = 0;
      paintingLaborDays = 0;
      paintingCost = 0;
    } else if (material === 'MDF_FILM' || category === 'sofa_bench') {
      // 인테리어 필름 시공 또는 소파/스툴 원단 래핑 인건비 (내장공 기준 적용)
      paintingLaborRate = ((costTaxonomy as any).labor_rates_krw?.['내장공']) || 256883;
      paintingLaborDays = Math.max(0.04, Math.round((surfaceM2 * 0.05) * 100) / 100);
      paintingCost = Math.round((paintingLaborDays * paintingLaborRate) + (category !== 'sofa_bench' ? surfaceM2 * 6_500 : 0)); // 필름은 자재비 합산, 소파는 이미 자재비에 원단 포함됨
    } else {
      // 일반 도장 (MDF_PAINT, 자작합판, 원목)
      paintingLaborRate = ((costTaxonomy as any).labor_rates_krw?.['도장공']) || 263017;
      paintingLaborDays = Math.max(0.02, Math.round((surfaceM2 * 0.035) * 100) / 100);
      paintingCost = Math.round((paintingLaborDays * paintingLaborRate) + (surfaceM2 * 5_500)); // 도료 자재비 합산
    }
    
    // 현장 세팅/설치 인건비 (보통인부[1002] 2026.1.1 기준 / costTaxonomy 반영)
    settingLaborRate = ((costTaxonomy as any).labor_rates_krw?.['보통인부']) || 172068;
    settingLaborDays = Math.max(0.05, Math.round(((volumeM3 * 0.1) + (surfaceM2 * 0.01)) * difficulty * 100) / 100);
    settingLaborCost = Math.round(settingLaborDays * settingLaborRate);
  } else {
    assemblyCost = calc(ratios.assembly);
    paintingCost = calc(ratios.painting);
    settingLaborCost = calc(ratios.settingLabor);
  }

  let outsourcingCost = calc(ratios.outsourcing);
  
  // 마감 자재별 외주 가공비 맥락 고도화
  if (material === 'stone' || material === 'glass') {
    // 대리석/유리는 외주 제단 및 연마 가공비가 집중됨 (도장이 없는 대신 가공비 크게 할증)
    outsourcingCost = Math.round(originCost * 0.25);
  } else if (material === 'metal') {
    // 금속은 절곡, 용접 등 1차 외주 비용
    outsourcingCost = Math.round(originCost * 0.15);
  }
  
  // 옵션 계산
  let optionsCost = 0;
  if (options?.hasHardware)  optionsCost += Math.round(originCost * 0.03 / 1000) * 1000; // 하드웨어: 원가의 3%
  if (options?.hasSocket)    optionsCost += 45000;  // 매립형 콘센트: 단독 4.5만원
  if (options?.hasLighting)  optionsCost += 85000;  // 간접조명 LED: 단독 8.5만원 (블랜드 LED 한 세트 기년 수준)
  if (options?.hasCushion)   optionsCost += Math.max(1, Math.round((options.w || 1000)/1000)) * 45000; // 인조가죽 쿠션: 미터당 4.5만원
  
  let packagingCost = 0;
  if (options?.w && options?.d && options?.h) {
    const wm = Math.max(options.w / 1000, 0.1);
    const dm = Math.max(options.d / 1000, 0.1);
    const hm = Math.max(options.h / 1000, 0.1);
    const volumeM3 = wm * dm * hm;
    const surfaceM2 = 2 * (wm * dm + wm * hm + dm * hm);
    
    // 포장 및 보양비 정밀 산출 (에어캡/스트레치 850원/㎡ + 골판지/보호가설 28,000원/㎥)
    const airCapCost = surfaceM2 * 850;
    const boardCost = volumeM3 * 28000;
    packagingCost = Math.round((airCapCost + boardCost) / 1000) * 1000;
  } else {
    packagingCost = calc(ratios.packaging);
  }
  let manufacturingTotal = assemblyCost + paintingCost + outsourcingCost + optionsCost + packagingCost;

  // 일반관리비 정확히 총원가의 지정비율
  let generalMgmtCost = calc(ratios.generalMgmt);

  let transportCost = 0;
  if (options?.w && options?.d && options?.h) {
    const volumeM3 = (options.w / 1000) * (options.d / 1000) * (options.h / 1000);
    // 화물차운전사[1049] 2026.1.1 기준: 238,936원
    // 부피 기반으로 차량 1대당 적재 비중 산출 (가정: 1대 10㎥) -> 소요 대수(Days)
    const transportDays = Math.max(0.02, Math.round((volumeM3 / 10) * 100) / 100);
    transportCost = Math.round(transportDays * 238936);
  } else {
    transportCost = calc(ratios.transport);
  }

  const loadingCost = calc(ratios.loading);
  let installTotal = transportCost + loadingCost + settingLaborCost;

  let miscExpenseCost = calc(ratios.miscExpense);
  let profitCost = calc(ratios.profit);
  
  if (options?.contractMode === 'included') {
    // 포함공사건 모드: 일관/이윤 총합을 노무비(assembly + painting + setting)와 경비(miscExpense)에 실질 비율대로 가중 분배
    const adminProfitSum = generalMgmtCost + profitCost;
    
    const laborTotal = assemblyCost + paintingCost + settingLaborCost;
    const overheadTotal = miscExpenseCost;
    const distributor = laborTotal + overheadTotal;
    
    if (distributor > 0) {
      const laborShare = Math.round(adminProfitSum * (laborTotal / distributor));
      const overheadShare = adminProfitSum - laborShare; // 남은 차액 모두
      
      // 노무비 내 세부 배분 (가중치)
      if (laborTotal > 0) {
        const aAdd = Math.round(laborShare * (assemblyCost / laborTotal));
        const pAdd = Math.round(laborShare * (paintingCost / laborTotal));
        const sAdd = laborShare - (aAdd + pAdd);
        
        assemblyCost += aAdd;
        paintingCost += pAdd;
        settingLaborCost += sAdd;
      }
      
      miscExpenseCost += overheadShare;
      
      // 일관/이윤은 0 처리
      generalMgmtCost = 0;
      profitCost = 0;
      
      // 재산정
      manufacturingTotal = assemblyCost + paintingCost + outsourcingCost + optionsCost + packagingCost;
      installTotal = transportCost + loadingCost + settingLaborCost;
    }
  }

  let totalOriginCost = materialTotal + manufacturingTotal + generalMgmtCost + installTotal + miscExpenseCost + profitCost;
  
  // 합계를 만원 단위로 강제 절사하고, 그 차액(단수차이)을 기업이윤에서 차감시켜 하위 항목의 총합과 총원가를 단 1원의 오차 없이 일치시킴
  const diff = totalOriginCost % 10000;
  if (options?.contractMode === 'included') {
    miscExpenseCost -= diff; // 이윤이 0이므로 경비에서 차감
  } else {
    profitCost -= diff;
  }
  totalOriginCost = materialTotal + manufacturingTotal + generalMgmtCost + installTotal + miscExpenseCost + profitCost;
  
  // 나라장터 87% 보정가
  const naraBidCost = Math.round((totalOriginCost * 0.87) / 1000) * 1000;
  
  // 견적서 종류에 따른 최종 단가 선정
  const netCost = estimateType === 'narajangteo' ? naraBidCost : totalOriginCost;

  return {
    material,
    materialLabel: MATERIAL_UNIT_PRICE[material].label,
    materialMultiplier: mult,
    basePrice,
    unitPricePerM2,
    materialSourcePage: MATERIAL_UNIT_PRICE[material].source.match(/p\.(\d+)/)?.[1] ?? '328',
    totalMaterialAreaM2: panelDetails.length > 0 ? panelDetails.reduce((sum, p) => sum + ((p.wMm/1000)*(p.dMm/1000)*p.qty), 0) : undefined,
    lossMultiplier: ['MDF_PB', 'PB', 'LPM', 'MDF_PAINT', 'MDF_FILM', 'plywood', 'oak_wood', 'lauan_wood', 'solid_wood'].includes(material) ? 1.18 : 1.10,
    rawMaterialCost,
    cuttingCost,
    edgeCost,
    materialTotal,
    assemblyLaborDays,
    assemblyLaborRate,
    assemblyCost,
    paintingLaborDays,
    paintingLaborRate,
    paintingCost,
    outsourcingCost,
    optionsCost,
    packagingCost,
    manufacturingTotal,
    generalMgmtCost,
    transportCost,
    loadingCost,
    settingLaborDays,
    settingLaborRate,
    settingLaborCost,
    installTotal,
    miscExpenseCost,
    profitCost,
    totalOriginCost,
    naraBidCost,
    netCost,
    appliedRates: {
      generalMgmt: ratios.generalMgmt,
      miscExpense: ratios.miscExpense,
      profit: ratios.profit
    },
    autoParts: panelDetails.map(p => {
      const aM2 = (p.wMm / 1000) * (p.dMm / 1000) * p.qty;
      const isWood = ['MDF_PB', 'PB', 'LPM', 'MDF_PAINT', 'MDF_FILM', 'plywood', 'oak_wood', 'lauan_wood', 'solid_wood'].includes(material);
      const lossRatio = isWood ? 1.18 : 1.10;
      return {
        partName: p.name,
        material: `${p.thickMm}T ${MATERIAL_UNIT_PRICE[material]?.label || material}`,
        w: p.wMm,
        d: p.dMm,
        h: p.thickMm,
        calculation: `${p.wMm}×${p.dMm} (x${p.qty}) → ${p.cost ? `${p.unitPrice?.toLocaleString()}원/㎡ × ${(aM2 * lossRatio).toFixed(3)}㎡(할증포함) = ${p.cost.toLocaleString()}원` : ''}`
      };
    })
  };
}

function parseMoneyLike(value: unknown): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : 0;
  const parsed = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
}

function scaleCostBreakdownToNetCost(cb: CostBreakdown, targetNetCost: number): CostBreakdown {
  const target = Math.max(0, Math.round(targetNetCost));
  const leafKeys = [
    'rawMaterialCost',
    'cuttingCost',
    'edgeCost',
    'assemblyCost',
    'paintingCost',
    'outsourcingCost',
    'optionsCost',
    'packagingCost',
    'generalMgmtCost',
    'transportCost',
    'loadingCost',
    'settingLaborCost',
    'miscExpenseCost',
    'profitCost',
  ] as const;

  const current = leafKeys.reduce((sum, key) => sum + ((cb as any)[key] || 0), 0);
  if (target === 0 || current <= 0) {
    for (const key of leafKeys) (cb as any)[key] = 0;
  } else {
    const factor = target / current;
    let acc = 0;
    for (let i = 0; i < leafKeys.length; i++) {
      const key = leafKeys[i];
      const value = i < leafKeys.length - 1
        ? Math.round((((cb as any)[key] || 0) * factor) / 1000) * 1000
        : target - acc;
      (cb as any)[key] = value;
      acc += value;
    }
  }

  cb.materialTotal = cb.rawMaterialCost + cb.cuttingCost + cb.edgeCost;
  cb.manufacturingTotal = cb.assemblyCost + cb.paintingCost + cb.outsourcingCost + cb.optionsCost + cb.packagingCost;
  cb.installTotal = cb.transportCost + cb.loadingCost + cb.settingLaborCost;
  cb.totalOriginCost = cb.materialTotal + cb.manufacturingTotal + cb.generalMgmtCost + cb.installTotal + cb.miscExpenseCost + cb.profitCost;

  const diff = target - cb.totalOriginCost;
  if (diff !== 0) {
    if (cb.profitCost > 0 || diff > 0) cb.profitCost += diff;
    else cb.miscExpenseCost += diff;
    cb.totalOriginCost = target;
  }

  cb.naraBidCost = Math.round((target * 0.87) / 1000) * 1000;
  cb.netCost = target;
  return cb;
}

// ══════════════════════════════════════════════
// 3. 카테고리 자동 분류
// ══════════════════════════════════════════════

/** 제품명에서 카테고리 자동 분류 */
export function classifyFurniture(name: string): string {
  const n = name.toLowerCase().replace(/\s+/g, '');
  
  // 우선순위 기반 매칭
  if (n.includes('무빙월') || n.includes('movingwall')) return 'moving_wall';
  if (n.includes('큐브서가') || n.includes('큐브')) return 'cube_shelf';
  if (n.includes('루버') || n.includes('그리드루버')) return 'louver';
  if (n.includes('누크') || n.includes('리딩누크') || n.includes('readingnook')) return 'reading_nook';
  if (n.includes('소파') || n.includes('벤치') || n.includes('의자') || n.includes('스툴')) return 'sofa_bench';
  if (n.includes('플랜트') || n.includes('플랜드') || n.includes('화분')) return 'planter';
  if (n.includes('수납장') || n.includes('캐비넷')) return 'cabinet';
  if (n.includes('전시용서가') || n.includes('전시서가') || n.includes('전시용')) return 'bookshelf_display';
  if (n.includes('안내데스크') || n.includes('업무데스크') || n.includes('데스크')) return 'desk';
  if (n.includes('테이블') || n.includes('열람')) return 'table';
  if (n.includes('서가') || n.includes('서랍') || n.includes('책장')) return 'bookshelf_wall';
  
  // 기본값
  return 'bookshelf_wall';
}

/** 카테고리 → PriceModel 연결 */
export function getPriceModel(category: string): PriceModel {
  return PRICE_MODELS.find(m => m.category === category) || PRICE_MODELS[0];
}

// ══════════════════════════════════════════════
// 4. 단가 계산
// ══════════════════════════════════════════════

/** 치수(mm) → 겉넓이(㎡) — 6면 표면적 기준 */
export function calcSurfaceArea(w: number, d: number, h: number, openRatio: number = 0): number {
  const wm = w / 1000, dm = d / 1000, hm = h / 1000;
  // 겉넓이 ㎡ = 2*(W*D + W*H + D*H). openRatio 강제 감산 안 함 — 내부 선반/디바이더가 면적 대체
  const rawSurface = 2 * (wm * dm + wm * hm + dm * hm);
  return Math.round(rawSurface * 10000) / 10000;
}

/**
 * 치수(mm) → 정면 투영 면적(㎡) — 전시용서가(FU-04) 전용
 * 깊이 150mm 얇은 OPEN형 서가는 6면적 기준 아닌 W×H 정면면적으로 단가 산출
 * 실제 제출가(FU-04 A~F) 역산 결과: avg≈154,500원/㎡ (정면투영면적 기준)
 */
export function calcFrontArea(w: number, h: number): number {
  return Math.round((w / 1000) * (h / 1000) * 10000) / 10000;
}

/** 치수(mm) → 체적(㎥) */
export function calcVolume(w: number, d: number, h: number): number {
  const wm = w / 1000, dm = d / 1000, hm = h / 1000;
  return Math.round(wm * dm * hm * 10000) / 10000;
}

/** 단가 계산 */
export function calcUnitPrice(
  w: number, d: number, h: number,
  category: string,
  grade: 'min' | 'avg' | 'high' | 'max' = 'avg',
  openRatio: number = 0,
  hasCurvedShape: boolean = false,
): { unitPrice: number; surfaceM2: number; basis: string } {
  const model = getPriceModel(category);
  const pricePerM2 = model.pricePerM2[grade];

  // ★ 전시용서가(bookshelf_display): 정면 투영면적(W×H) 기준 단가 산출
  // FU-04 실제 제출가 역산: 깊이 150mm OPEN형은 6면적 계산 시 단가 과대 계산됨
  const isDisplayShelf = category === 'bookshelf_display';
  const surfaceM2 = isDisplayShelf
    ? calcFrontArea(w, h) // W×H 정면면적
    : calcSurfaceArea(w, d, h, openRatio); // 일반 6면 표면적

  let rawPrice = surfaceM2 * pricePerM2;

  // 곡면/R가공 할증: CNC 가공비 + 자재 로스 + 조립 난이도 15% 가산
  const curvedSurcharge = hasCurvedShape ? 0.15 : 0;
  rawPrice *= (1 + curvedSurcharge);

  const unitPrice = Math.round(rawPrice / 10000) * 10000; // 만원 단위 반올림

  let basis = isDisplayShelf
    ? `${model.label} ${grade}단가 ${pricePerM2.toLocaleString()}원/㎡ × ${surfaceM2}㎡(W×H 정면면적)`
    : `${model.label} ${grade}단가 ${pricePerM2.toLocaleString()}원/㎡ × ${surfaceM2}㎡`;
  if (openRatio > 0 && !isDisplayShelf) {
    basis += ` (OPEN ${Math.round(openRatio * 100)}% 공제)`;
  }
  if (hasCurvedShape) {
    basis += ` (곡면R가공 +15% 할증)`;
  }

  return {
    unitPrice,
    surfaceM2,
    basis,
  };
}

// ══════════════════════════════════════════════
// 5. 도면에서 가구 추출 (DXF 텍스트 파싱)
// ══════════════════════════════════════════════

export interface RawFurnitureBlock {
  name: string;
  code: string;
  location: string;
  w: number;
  d: number;
  h: number;
  qty: number;
}

/**
 * DXF 텍스트에서 가구 블록 추출
 * INSERT 블록 중 FU- 접두사 또는 가구 키워드 포함 블록 탐색
 */
export function extractFurnitureFromDxf(dxfText: string): RawFurnitureBlock[] {
  const blocks: RawFurnitureBlock[] = [];
  const lines = dxfText.split(/\r?\n/);
  
  // 1) INSERT + ATTRIB 기반 추출
  let inInsert = false;
  let blockName = '';
  let blockLayer = '';
  let attrs: Record<string, string> = {};
  
  for (let i = 0; i < lines.length; i++) {
    const code = parseInt(lines[i].trim());
    const value = lines[i + 1]?.trim() || '';
    
    if (code === 0 && value === 'INSERT') {
      if (inInsert && blockName) {
        processBlock(blockName, blockLayer, attrs, blocks);
      }
      inInsert = true;
      blockName = '';
      blockLayer = '';
      attrs = {};
    } else if (code === 0 && value === 'ATTRIB' && inInsert) {
      // ATTRIB는 INSERT 바로 다음
    } else if (code === 0 && inInsert && value !== 'ATTRIB') {
      if (blockName) processBlock(blockName, blockLayer, attrs, blocks);
      inInsert = false;
      blockName = '';
      attrs = {};
    }
    
    if (inInsert) {
      if (code === 2) blockName = value;
      if (code === 8) blockLayer = value;
    }
    
    // ATTRIB TAG/VALUE
    if (code === 2 && inInsert) attrs['_block'] = value;
    if (code === 1 && inInsert) {
      // 마지막 TAG에 대한 VALUE
    }
  }
  if (inInsert && blockName) processBlock(blockName, blockLayer, attrs, blocks);
  
  // 2) 텍스트 기반 가구 스케줄 테이블 파싱 (TEXT 엔티티에서)
  const textPattern = /^(FU-\d+)\s+(.+?)\s+(\d+)\s*[×xX*]\s*(\d+)\s*[×xX*]\s*(\d+)/;
  for (let i = 0; i < lines.length; i++) {
    const code = parseInt(lines[i].trim());
    const value = lines[i + 1]?.trim() || '';
    
    if (code === 1) {
      const match = value.match(textPattern);
      if (match) {
        blocks.push({
          name: match[2],
          code: match[1],
          location: '',
          w: parseInt(match[3]),
          d: parseInt(match[4]),
          h: parseInt(match[5]),
          qty: 1,
        });
      }
    }
  }
  
  return blocks;
}

function processBlock(
  blockName: string, layer: string,
  attrs: Record<string, string>,
  blocks: RawFurnitureBlock[],
): void {
  // FU- 코드 블록만 처리
  if (!blockName.toUpperCase().startsWith('FU-') && !isFurnitureKeyword(blockName)) return;
  
  const w = parseFloat(attrs['W'] || attrs['폭'] || '0');
  const d = parseFloat(attrs['D'] || attrs['깊이'] || '0');
  const h = parseFloat(attrs['H'] || attrs['높이'] || '0');
  
  blocks.push({
    name: blockName,
    code: blockName.match(/FU-\d+/)?.[0] || blockName,
    location: layer || '',
    w, d, h,
    qty: 1,
  });
}

function isFurnitureKeyword(name: string): boolean {
  const keywords = ['서가', '데스크', '테이블', '수납장', '소파', '루버', '플랜트', '누크', '큐브', '무빙', '벤치'];
  return keywords.some(kw => name.includes(kw));
}

// ══════════════════════════════════════════════
// 5-B. 제조 분해 엔진 (Manufacturing Decomposition)
// ══════════════════════════════════════════════
// 원판 4×8 (1220×2440mm), 엘리베이터 양중, 로스율 반영

/** 원판 규격 (4' × 8') */
const RAW_SHEET_W = 1220;
const RAW_SHEET_H = 2440;
const RAW_SHEET_AREA_M2 = (RAW_SHEET_W / 1000) * (RAW_SHEET_H / 1000); // 2.9768 ㎡

/** 엘리베이터 양중 한계 (화물용 EV, mm) */
const ELEVATOR_LIMIT = { w: 2100, d: 1400, h: 2400 };

/** 네스팅 효율 (82% 활용 = 18% 로스) */
const NESTING_EFFICIENCY = 0.82;

/** 이음 접합 허용치 (mm) */
const JOINT_ALLOWANCE = 3;

export interface MfgPanel {
  partName: string;
  material: string;
  cutW: number;
  cutH: number;
  thickness: number;
  qty: number;
  fromPart: string;
  splitNote: string;
  areaM2: number;
  sheetsNeeded: number;
  phase: 'factory' | 'site';
  step: number;
  stepNote: string;
}

export interface MfgModule {
  moduleNo: number;
  name: string;
  width: number;
  depth: number;
  height: number;
  fitsElevator: boolean;
  panels: MfgPanel[];
  factorySteps: string[];
  siteSteps: string[];
}

export interface ManufacturingPlan {
  modules: MfgModule[];
  totalPanels: number;
  totalSheets: number;
  totalCutAreaM2: number;
  totalSheetAreaM2: number;
  wasteAreaM2: number;
  wastePercent: number;
  assemblyOverview: string[];
}

/** 재질 문자열에서 두께(mm) 추출 */
function extractThickness(material: string | undefined): number {
  if (!material) return 18;
  const m = material.match(/(\d+)\s*(?:T|MM|mm)/i);
  return m ? parseInt(m[1]) : 18;
}

/**
 * AI calcParts + 전체 치수 → 현실적 공장제작 + 현장설치 계획
 * 핵심 원리: 높이가 원판(2440mm)을 초과하면 개별 패널 분할이 아니라
 * 전체 모듈을 상하 ㅁ자 박스(Box Unit)로 나누어 각각 공장 조립 후 현장 볼트 체결
 */
export function decomposeForManufacturing(
  calcParts: Array<{partName: string; material: string; w: number; d: number; h: number; calculation: string}>,
  overallW: number,
  overallD: number,
  overallH: number,
  openRatio: number = 0, // OPEN 비율 (0~1). OPEN 영역은 뒷판 없음
  moduleBreakdown?: { label?: string; width_mm: number }[] // 명시적 모듈 분할 데이터
): ManufacturingPlan {

  // ── 1. 역할 분류 ──
  type Role = 'divider' | 'shelf' | 'door' | 'back' | 'top_bottom' | 'lighting' | 'other';
  const classified = calcParts.map(p => {
    const n = (p.partName || '').toLowerCase();
    // ★ 수량 파싱: "(x5개)", "×5개", "5개" 등 명시적 패턴에서만 추출. 치수 숫자(1165mm 등)를 수량으로 오인식하지 않도록 진단 뒤 (x\d)만 보는 돀한 패턴 사용
    const calc = p.calculation || '';
    // 패턴 우선순위: \(x\d+\uac1c\) > x(\d+)개 > "\d+개" at end-of-token only
    const qtyMatch =
      calc.match(/\(x(\d{1,2})\uac1c\)/i) ||
      calc.match(/[\u00d7x](\d{1,2})\uac1c/i) ||
      calc.match(/\[?(\d{1,2})\]?\s*(?:개|ea)\b/i);
    // 팀운: qty는 최대 99개로 cap (치수 숫자 1165 등 오인식 방지)
    const qty = qtyMatch ? Math.min(99, parseInt(qtyMatch[1])) : 1;
    let role: Role = 'other';
    if (n.includes('측판') || n.includes('칸막이') || n.includes('수직') || n.includes('기둥')) role = 'divider';
    else if (n.includes('선반')) role = 'shelf';
    else if (n.includes('도어') || n.includes('문짝')) role = 'door';
    else if (n.includes('뒷판') || n.includes('배면')) role = 'back';
    else if (n.includes('바닥') || n.includes('천장') || n.includes('상판') || n.includes('하판')) role = 'top_bottom';
    else if (n.includes('led') || n.includes('조명')) role = 'lighting';
    return { part: p, role, qty };
  });

  // ── 2. 수직 Bay 수 결정 ──
  const dividers = classified.filter(c => c.role === 'divider');
  // 좌우측 2개는 기본이므로, 3개 이상일 때부터 내부 칸막이로 보고 Bay 수 계산
  const dividerCount = dividers.reduce((s, d) => s + d.qty, 0);
  // ★ Bay 수 최대 50개 cap — 치수 파싱 오류로 초겨단위 바이가 되는 현상 방지
  let bayCount = Math.min(50, Math.max(1, dividerCount > 1 ? dividerCount - 1 : 1));

  let customBays: number[] = [];
  // ★ arguments[5] 제거 — 일반 함수에서 arguments는 움직일 수 있음
  const validModuleBreakdown = moduleBreakdown || [];

  if (validModuleBreakdown && validModuleBreakdown.length > 0) {
    // 🚨사용자가 제공한 특정 Bay 폭 데이터가 있는 경우 우선 적용!
    customBays = validModuleBreakdown.map((m: any) => m.width_mm).filter((w: number) => w > 0);
    if (customBays.length > 0) {
      bayCount = customBays.length;
    }
  }
  
  if (customBays.length === 0) {
    // 🚨 CRITICAL RULE: 원판 최대폭 1220 초과 시 무조건 분할 강제 🚨
    if (overallW > 1200) {
      // 900mm 최적 모듈 단위로 강제 분할
      const optimalBays = Math.ceil(overallW / 900);
      if (bayCount < optimalBays) {
        bayCount = optimalBays;
      }
    }
  }
  
  // 방어 코드: bayCount가 혹시라도 0이 되는 것을 방지
  bayCount = Math.max(1, bayCount);
  const defaultBayWidth = Math.round(overallW / bayCount);

  // ── 3. 상하 BOX 분할 결정 ──
  const mainMat = dividers[0]?.part.material || '20T LPM 마감';
  const mainThk = extractThickness(mainMat);
  // 하부 BOX 최대 높이: 원판 장축(2440) - 상하 판재 두께 여유
  const MAX_BOX_H = RAW_SHEET_H - mainThk * 2 - JOINT_ALLOWANCE * 2; // ~2400mm 내외
  const needsVertSplit = overallH > MAX_BOX_H;
  const lowerBoxH = needsVertSplit ? Math.min(overallH, MAX_BOX_H) : overallH;
  const upperBoxH = needsVertSplit ? overallH - lowerBoxH : 0;

  // ── 4. 선반/도어 총량 파악 및 Bay당 배분 ──
  const shelves = classified.filter(c => c.role === 'shelf');
  const totalShelves = shelves.reduce((s, c) => s + c.qty, 0);
  const shelvesPerBay = Math.max(1, Math.ceil(totalShelves / bayCount));
  const shelfMat = shelves[0]?.part.material || mainMat;
  const shelfThk = shelves[0] ? extractThickness(shelves[0].part.material) : mainThk;
  const doors = classified.filter(c => c.role === 'door');
  const totalDoors = doors.reduce((s, d) => s + d.qty, 0);
  const doorsPerBay = doors.length > 0 ? Math.ceil(totalDoors / bayCount) : 0;
  const backParts = classified.filter(c => c.role === 'back');
  const backThk = backParts[0] ? extractThickness(backParts[0].part.material) : 9;
  const backMat = backParts[0]?.part.material || '9T LPM 마감';

  const canFitElevator = (w: number, d: number, h: number) => {
    const dims = [w, d, h].sort((a, b) => a - b);
    return dims[0] <= ELEVATOR_LIMIT.d && dims[1] <= ELEVATOR_LIMIT.w && dims[2] <= ELEVATOR_LIMIT.h;
  };

  // ── 5. 단일 ㅁ자 박스 구성 함수 ──
  function buildBox(
    label: string, boxW: number, boxD: number, boxH: number,
    shelfCount: number, doorCount: number, stepStart: number,
    isOpen: boolean = false, // OPEN 영역이면 뒷판 미시공
  ): { panels: MfgPanel[]; fSteps: string[]; nextStep: number } {
    const panels: MfgPanel[] = [];
    const fSteps: string[] = [];
    let sc = stepStart;
    const innerH = boxH - mainThk * 2; // 바닥+천장 두께 차감
    const innerW = boxW - mainThk * 2; // 좌우 측판 두께 차감

    // 원판 재단 면적 계산 헬퍼
    const mkPanel = (name: string, mat: string, w: number, h: number, thk: number, qty: number, note: string, phase: 'factory' | 'site'): MfgPanel => {
      const areaM2 = (w / 1000) * (h / 1000) * qty;
      const fitsSheet = Math.max(w, h) <= RAW_SHEET_H && Math.min(w, h) <= RAW_SHEET_W;
      return {
        partName: name, material: mat, cutW: w, cutH: h, thickness: thk, qty,
        fromPart: name,
        splitNote: fitsSheet ? '단일 원판 재단' : '⚠ 원판 초과 — 검토 필요',
        areaM2: Math.round(areaM2 * 10000) / 10000,
        sheetsNeeded: Math.ceil(areaM2 / (RAW_SHEET_AREA_M2 * NESTING_EFFICIENCY)),
        phase, step: sc, stepNote: note,
      };
    };

    // ① 바닥판
    panels.push(mkPanel('바닥판', mainMat, boxW, boxD, mainThk, 1, '① 바닥판 수평 배치', 'factory'));
    fSteps.push('① 바닥판 수평 배치'); sc++;

    // ② 좌측판
    panels.push(mkPanel('측판(좌)', mainMat, boxD, innerH, mainThk, 1, '② 좌측판 수직 (다보+본드)', 'factory'));
    fSteps.push('② 좌측판 수직 고정 (다보+목공본드)'); sc++;

    // ③ 우측판
    panels.push(mkPanel('측판(우)', mainMat, boxD, innerH, mainThk, 1, '③ 우측판 수직 고정', 'factory'));
    fSteps.push('③ 우측판 수직 고정'); sc++;

    // ④ 중간선반
    if (shelfCount > 0) {
      // OPEN이면 뒷판 없으므로 선반 깊이 = 박스 깊이 전체
      const shD = isOpen ? boxD : boxD - mainThk;
      panels.push(mkPanel('중간선반', shelfMat, innerW, shD, shelfThk, shelfCount,
        '④ 선반 ' + shelfCount + '단 (캠볼트/피스)', 'factory'));
      fSteps.push('④ 선반 ' + shelfCount + '단 내부 고정'); sc++;
    }

    // ⑤ 천장판
    panels.push(mkPanel('천장판', mainMat, boxW, boxD, mainThk, 1, '⑤ 천장판 상부 고정', 'factory'));
    fSteps.push('⑤ 천장판 상부 고정'); sc++;

    // ⑥ 뒷판 (OPEN 영역은 뒷판 미시공)
    if (!isOpen) {
      panels.push(mkPanel('뒷판', backMat, innerW, innerH, backThk, 1, '⑥ 뒷판 후면 (타카+본드)', 'factory'));
      fSteps.push('⑥ 뒷판 후면 접합 (에어타카+목공본드)'); sc++;
    } else {
      fSteps.push('⑥ [OPEN] 뒷판 미시공 — 벽면 직접 노출'); sc++;
    }

    // ⑦ 엣지밴딩
    fSteps.push('⑦ ABS 엣지밴딩 마감'); sc++;

    // 도어 (현장 설치)
    if (doorCount > 0) {
      const doorW = doors[0]?.part.w || Math.round(innerW / 2);
      const doorH = doors[0]?.part.h && doors[0].part.h > 100 ? Math.min(doors[0].part.h, innerH / 2) : Math.round(innerH / shelfCount);
      const doorMat = doors[0]?.part.material || mainMat;
      panels.push(mkPanel('도어', doorMat, doorW, doorH, extractThickness(doorMat), doorCount,
        '현장: 도어 경첩 설치', 'site'));
    }

    return { panels, fSteps, nextStep: sc };
  }

  // ── 6. 모듈 (Bay) 생성 ──
  const modules: MfgModule[] = [];
  let globalStep = 1;

  for (let b = 0; b < bayCount; b++) {
    const bW = (customBays && customBays[b]) ? customBays[b] : ((b === bayCount - 1 && customBays.length === 0) ? overallW - (defaultBayWidth * (bayCount - 1)) : defaultBayWidth);
    let bayLabel = bayCount > 1 ? (b === 0 ? '좌측' : b === bayCount - 1 ? '우측' : '중간') : '';
    if (validModuleBreakdown[b] && validModuleBreakdown[b].label) {
      bayLabel = validModuleBreakdown[b]?.label || "";
    }
    const sSteps: string[] = [];

    // 상하 분할이 필요하면 하부+상부 두 개의 서브모듈로 구성
    if (needsVertSplit) {
      // 선반을 상하 분배 (높이 비율)
      const lowerRatio = lowerBoxH / overallH;
      const lowerShelves = Math.max(1, Math.round(shelvesPerBay * lowerRatio));
      const upperShelves = Math.max(1, shelvesPerBay - lowerShelves);
      const lowerDoors = Math.round(doorsPerBay * lowerRatio);
      const upperDoors = doorsPerBay - lowerDoors;

      // 하부 BOX
      const lower = buildBox('하부', bW, overallD, lowerBoxH, lowerShelves, lowerDoors, globalStep);
      globalStep = lower.nextStep;

      // 상부 BOX
      const upper = buildBox('상부', bW, overallD, upperBoxH, upperShelves, upperDoors, globalStep);
      globalStep = upper.nextStep;

      const allPanels = [...lower.panels, ...upper.panels];
      const allFSteps = [
        '── 하부 BOX (' + bW + '×' + overallD + '×' + lowerBoxH + ') ──',
        ...lower.fSteps,
        '── 상부 BOX (' + bW + '×' + overallD + '×' + upperBoxH + ') ──',
        ...upper.fSteps,
        '⑧ 보양 포장 (에어캡+코너보호)',
      ];

      sSteps.push('⑨ 하부BOX 벽면 배치 (수평조정)');
      sSteps.push('⑩ 상부BOX 하부 위 올림');
      sSteps.push('⑪ 상하 볼트 체결 (4~8ea)');
      sSteps.push('⑫ 좌우 모듈 연결 볼트');
      if (doorsPerBay > 0) sSteps.push('⑬ 도어 경첩 설치+조정');
      sSteps.push('⑭ 마감 트림·벽고정 앙카');

      const evLower = canFitElevator(bW, overallD, lowerBoxH);
      const evUpper = canFitElevator(bW, overallD, upperBoxH);
      if (!evLower) sSteps.push('⚠ 하부BOX EV초과 → 뉘어서 운반');
      if (!evUpper) sSteps.push('⚠ 상부BOX EV초과');

      modules.push({
        moduleNo: b + 1,
        name: 'Bay ' + (b + 1) + (bayLabel ? ' (' + bayLabel + ')' : '') + ' — 상하 ㅁ자 분할',
        width: bW, depth: overallD, height: overallH,
        fitsElevator: evLower && evUpper,
        panels: allPanels,
        factorySteps: allFSteps,
        siteSteps: sSteps,
      });
    } else {
      // 높이가 원판 이내 → 단일 BOX
      const box = buildBox('', bW, overallD, overallH, shelvesPerBay, doorsPerBay, globalStep);
      globalStep = box.nextStep;
      box.fSteps.push('⑧ 보양 포장');

      sSteps.push('⑨ 모듈 벽면 배치 (수평조정)');
      sSteps.push('⑩ 좌우 모듈 연결 볼트');
      if (doorsPerBay > 0) sSteps.push('⑪ 도어 경첩 설치');
      sSteps.push('⑫ 마감 트림·벽고정 앙카');

      const evFit = canFitElevator(bW, overallD, overallH);
      if (!evFit) sSteps.push('⚠ EV양중 불가 → 뉘어서 운반');

      modules.push({
        moduleNo: b + 1,
        name: 'Bay ' + (b + 1) + (bayLabel ? ' (' + bayLabel + ')' : '') + ' — 단일 ㅁ자',
        width: bW, depth: overallD, height: overallH,
        fitsElevator: evFit,
        panels: box.panels,
        factorySteps: box.fSteps,
        siteSteps: sSteps,
      });
    }
  }

  // ── 7. 합산 ──
  const allPanels = modules.flatMap(m => m.panels);
  const totalPanels = allPanels.reduce((s, p) => s + p.qty, 0);
  const totalCutAreaM2 = allPanels.reduce((s, p) => s + p.areaM2, 0);
  const totalSheets = allPanels.reduce((s, p) => s + p.sheetsNeeded, 0);
  const totalSheetAreaM2 = Math.round(totalSheets * RAW_SHEET_AREA_M2 * 100) / 100;
  const wasteAreaM2 = Math.round((totalSheetAreaM2 - totalCutAreaM2) * 100) / 100;
  const wastePercent = totalSheetAreaM2 > 0 ? Math.round(wasteAreaM2 / totalSheetAreaM2 * 100) : 0;

  const boxDesc = needsVertSplit
    ? '하부(' + lowerBoxH + 'mm) + 상부(' + upperBoxH + 'mm) ㅁ자 적재'
    : '단일 ㅁ자 박스(' + overallH + 'mm)';

  return {
    modules,
    totalPanels,
    totalSheets,
    totalCutAreaM2: Math.round(totalCutAreaM2 * 100) / 100,
    totalSheetAreaM2,
    wasteAreaM2,
    wastePercent,
    assemblyOverview: [
      '📦 원판(4×8) ' + totalSheets + '장 소요 (재단 ' + totalCutAreaM2.toFixed(2) + '㎡ / 원판 ' + totalSheetAreaM2.toFixed(2) + '㎡, 로스 ' + wastePercent + '%)',
      '🏭 공장: ' + bayCount + 'Bay × ' + boxDesc,
      '📐 모듈당 Bay폭 ~' + defaultBayWidth + '×' + overallD + 'mm (원판 1220 이내 ✓)',
      modules.every(m => m.fitsElevator)
        ? '✅ 전 모듈 EV양중 가능 (뉘어서 탑재)'
        : '⚠ 일부 모듈 EV초과 — 외부 리프트 필요',
      '🔧 현장: ' + (needsVertSplit ? '하부배치→상부적재→볼트체결' : '모듈배치→볼트연결') + '→도어→마감트림',
    ],
  };
}

// ══════════════════════════════════════════════
// 6. Excel 견적서 파싱 (기존 견적서 학습)
// ══════════════════════════════════════════════

export interface ParsedEstimateItem {
  no: number;
  name: string;
  type: string;
  code: string;
  w: number;
  d: number;
  h: number;
  qty: number;
  unitPrice: number;
  totalPrice: number;
  surfaceM2: number;
  volumeM3: number;
  pricePerM2: number;
  category: string;
}

/**
 * XLS/XLSX 견적서 파싱 (SheetJS)
 * 디자인인사이트 표준 양식 기준
 */
export function parseEstimateExcel(data: any[][]): ParsedEstimateItem[] {
  const items: ParsedEstimateItem[] = [];
  
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    if (!row) continue;
    const no = parseInt(String(row[0]));
    if (isNaN(no) || no < 1) continue;
    
    const wStr = String(row[4] || '0');
    const dStr = String(row[5] || '0');
    const hStr = String(row[6] || '0');
    
    const w = parseFloat(wStr.split('/')[0]) || 0;
    const d = parseFloat(dStr.split('/')[0]) || 0;
    const h = parseFloat(hStr.split('/')[0]) || 0;
    
    if (w === 0 && d === 0 && h === 0) continue;
    
    const name = String(row[1] || '').trim();
    const unitPrice = parseInt(String(row[8] || '0')) || 0;
    const totalPrice = parseInt(String(row[9] || '0')) || 0;
    const surfaceM2 = calcSurfaceArea(w, d, h);
    const volumeM3 = calcVolume(w, d, h);
    
    items.push({
      no,
      name,
      type: String(row[2] || '').trim(),
      code: String(row[3] || '').trim(),
      w, d, h,
      qty: parseInt(String(row[7] || '1')) || 1,
      unitPrice,
      totalPrice,
      surfaceM2,
      volumeM3,
      pricePerM2: surfaceM2 > 0 ? Math.round(unitPrice / surfaceM2) : 0,
      category: classifyFurniture(name),
    });
  }
  
  return items;
}

// ══════════════════════════════════════════════
// 7. 자동 견적서 생성 엔진
// ══════════════════════════════════════════════

export interface EstimateInput {
  projectName: string;
  items: {
    name: string;
    code?: string;
    fu?: string;
    location?: string;
    w: number;  // mm
    d: number;  // mm
    h: number;  // mm
    qty: number;
    unitPrice?: number | string;
    unit_price?: number | string;
    up?: number | string;
    hasHardware?: boolean;
    hasSocket?: boolean;
    calcParts?: { partName: string; material: string; w?: number; d?: number; h?: number; calculation: string }[];
    moduleBreakdown?: { label?: string; width_mm: number }[];
  }[];
  priceGrade?: 'min' | 'avg' | 'high' | 'max';
  priceLevel?: 'model' | 'hamyang_bid';
  estimateType?: 'designinsight' | 'narajangteo' | 'compare_3';
  customRatios?: Partial<typeof COST_RATIOS>;
  customMaterialMultipliers?: Partial<typeof MATERIAL_MULTIPLIER>;
  recipient?: string;
  useRounding?: boolean;
}

/**
 * 메인 API: 가구 목록 → 자동 견적서 생성
 */
export function generateEstimate(input: EstimateInput): FurnitureEstimate {
  // 견적서 종류에 따른 단가 등급 보정
  let grade = input.priceGrade || 'avg';
  const estimateType = input.estimateType || 'designinsight';
  const priceLevel = input.priceLevel || 'hamyang_bid';
  
  if (estimateType === 'narajangteo') {
    // 나라장터는 보통 최고가 기준 적용
    grade = 'max';
  }

  const warnings: string[] = [];
  const items: FurnitureItem[] = [];
  
  input.items.forEach((raw, idx) => {
    const category = classifyFurniture(raw.name);
    const openRatio = (raw as any).openRatio || 0;
    const hasCurvedShape = !!(raw as any).hasCurvedShape;
    const { unitPrice: rawPrice, surfaceM2, basis } = calcUnitPrice(raw.w, raw.d, raw.h, category, grade, openRatio, hasCurvedShape);
    const volumeM3 = calcVolume(raw.w, raw.d, raw.h);
    
    if (surfaceM2 < 0.1) {
      warnings.push(`⚠ #${idx + 1} "${raw.name}": 겉넓이 ${surfaceM2}㎡ — 치수 확인 필요`);
    }
    
    // 동연디자인 레퍼런스 사이즈 검증
    const sizeCheck = validateFurnitureSize(raw.w, raw.d, raw.h, category);
    if (!sizeCheck.valid) {
      sizeCheck.warnings.forEach(w => warnings.push(`📐 #${idx + 1} "${raw.name}": ${w}`));
    }
    
    // 재질 추정 + 상세 원가 계산
    // finishMaterial 드롭다운 → MaterialType 매핑
    const finishMaterialMap: Record<string, MaterialType> = {
      'PB': 'PB', 'LPM': 'LPM', 'MDF(도장)': 'MDF_PAINT', 'MDF(필름)': 'MDF_FILM',
      '자작합판': 'plywood', '오크집성판': 'oak_wood', '라왕집성판': 'lauan_wood',
    };
    const finishMaterial = (raw as any).finishMaterial as string | undefined;
    const materialInput = (finishMaterial && finishMaterialMap[finishMaterial])
      || ((raw as any).material as MaterialType | undefined);
    const material = materialInput || estimateMaterial(raw.name, category);
    // 선반 단수: 사용자 지정 우선 → AI 자동 추천 폴백
    const shelfCount = (raw as any).shelfCount != null
      ? Number((raw as any).shelfCount)
      : recommendShelfCount(raw.h, raw.name);

    let cb = calcCostBreakdown(
      rawPrice * sizeCheck.extrapolationMultiplier,
      material,
      estimateType,
      input.customRatios,
      input.customMaterialMultipliers,
      { hasHardware: raw.hasHardware, hasSocket: raw.hasSocket, hasLighting: (raw as any).hasLighting, hasCushion: (raw as any).hasCushion, thickness: (raw as any).thickness, shelfCount, w: raw.w, d: raw.d, h: raw.h, category }
    );

    const sourceUnitPrice = parseMoneyLike((raw as any).unitPrice ?? (raw as any).unit_price ?? (raw as any).up);
    if (sourceUnitPrice > 0) {
      cb = scaleCostBreakdownToNetCost(cb, sourceUnitPrice);
    } else if (priceLevel === 'hamyang_bid' && estimateType === 'designinsight') {
      const multiplier = BID_LEVEL_CATEGORY_MULTIPLIER[category] ?? 1;
      cb = scaleCostBreakdownToNetCost(cb, Math.round((cb.netCost * multiplier) / 10000) * 10000);
    }

    // 하위 내역 정합성이 완벽한 cb.netCost를 단위 단가로 그대로 사용
    const unitPrice = cb.netCost;
    
    // 시중 비교 단가 동적 생성 (현실적 비율 기반 모델링)
    const marketComparison = {
      naraPrice: Math.floor(unitPrice * 1.053 / 1000) * 1000, // 등록가의 95% 수준이 되도록 역산 산출 (1.053 배)
      naraUrl: `https://shopping.g2b.go.kr/sm/srch/totalSearch.do?kwd=${encodeURIComponent(raw.name)}`,
      fursysPrice: Math.floor(unitPrice * 1.15 / 1000) * 1000,
      livartPrice: Math.floor(unitPrice * 1.08 / 1000) * 1000,
      koaPrice: Math.floor(unitPrice * 1.02 / 1000) * 1000,
    };
    
    items.push({
      no: idx + 1,
      name: raw.name,
      code: raw.code || `FU-${String(idx + 1).padStart(2, '0')}`,
      location: raw.location || '',
      w: raw.w,
      d: raw.d,
      h: raw.h,
      qty: raw.qty,
      thickness: (raw as any).thickness,
      category,
      surfaceM2,
      volumeM3,
      unitPrice,
      totalPrice: unitPrice * raw.qty,
      priceGrade: grade,
      basis: `${basis} → ${cb.materialLabel}(×${cb.materialMultiplier})${sourceUnitPrice > 0 ? ' · 원견적 단가 우선 적용' : (priceLevel === 'hamyang_bid' && estimateType === 'designinsight' ? ` · 함양 입찰레벨 보정×${(BID_LEVEL_CATEGORY_MULTIPLIER[category] ?? 1).toFixed(4)}` : '')}`,
      costBreakdown: cb,
      hasHardware: raw.hasHardware,
      hasSocket: raw.hasSocket,
      calcParts: (raw as any).calcParts && (raw as any).calcParts.length > 0
        ? (raw as any).calcParts
        : cb.autoParts,
      marketComparison,
    });
  });
  
  const subtotal = items.reduce((s, i) => s + i.totalPrice, 0);
  const vat = Math.floor(subtotal * 0.1);
  
  // 카테고리별 소계
  const catBreakdown = new Map<string, { label: string; count: number; qty: number; amount: number }>();
  for (const item of items) {
    const model = getPriceModel(item.category);
    const existing = catBreakdown.get(item.category) || { label: model.label, count: 0, qty: 0, amount: 0 };
    existing.count++;
    existing.qty += item.qty;
    existing.amount += item.totalPrice;
    catBreakdown.set(item.category, existing);
  }
  
  // 원가 구성 요약
  const costSummary = {
    // 세부 내역
    totalRawMaterial: items.reduce((s, i) => s + i.costBreakdown.rawMaterialCost * i.qty, 0),
    totalCutting: items.reduce((s, i) => s + i.costBreakdown.cuttingCost * i.qty, 0),
    totalEdge: items.reduce((s, i) => s + i.costBreakdown.edgeCost * i.qty, 0),
    totalAssembly: items.reduce((s, i) => s + i.costBreakdown.assemblyCost * i.qty, 0),
    totalPainting: items.reduce((s, i) => s + i.costBreakdown.paintingCost * i.qty, 0),
    totalOutsourcing: items.reduce((s, i) => s + i.costBreakdown.outsourcingCost * i.qty, 0),
    totalPackaging: items.reduce((s, i) => s + i.costBreakdown.packagingCost * i.qty, 0),
    totalGeneralMgmt: items.reduce((s, i) => s + i.costBreakdown.generalMgmtCost * i.qty, 0),
    totalTransport: items.reduce((s, i) => s + i.costBreakdown.transportCost * i.qty, 0),
    totalLoading: items.reduce((s, i) => s + i.costBreakdown.loadingCost * i.qty, 0),
    totalSettingLabor: items.reduce((s, i) => s + i.costBreakdown.settingLaborCost * i.qty, 0),
    totalMiscExpense: items.reduce((s, i) => s + i.costBreakdown.miscExpenseCost * i.qty, 0),
    totalProfit: items.reduce((s, i) => s + i.costBreakdown.profitCost * i.qty, 0),
    
    // 집계 (Aggregate)
    totalMaterial: items.reduce((s, i) => s + i.costBreakdown.materialTotal * i.qty, 0),
    totalManufacturing: items.reduce((s, i) => s + i.costBreakdown.manufacturingTotal * i.qty, 0),
    totalLogisticsInstall: items.reduce((s, i) => s + i.costBreakdown.installTotal * i.qty, 0),
    totalOptions: items.reduce((s, i) => s + (i.costBreakdown.optionsCost || 0) * i.qty, 0),
  };
  
  return {
    projectName: input.projectName,
    generatedAt: new Date().toISOString(),
    estimateId: 'EST-' + new Date().toISOString().slice(0,10).replace(/-/g,'') + '-' + Math.floor(Math.random() * 9000 + 1000),
    recipient: input.recipient,
    totalItems: items.length,
    totalQty: items.reduce((s, i) => s + i.qty, 0),
    subtotal,
    vat,
    grandTotal: subtotal + vat,
    discount: input.useRounding ? (subtotal + vat) - (Math.floor((subtotal + vat) / ((subtotal + vat) >= 10_000_000 ? 1_000_000 : 10_000)) * ((subtotal + vat) >= 10_000_000 ? 1_000_000 : 10_000)) : 0,
    finalGrandTotal: input.useRounding ? Math.floor((subtotal + vat) / ((subtotal + vat) >= 10_000_000 ? 1_000_000 : 10_000)) * ((subtotal + vat) >= 10_000_000 ? 1_000_000 : 10_000) : subtotal + vat,
    items,
    categoryBreakdown: Array.from(catBreakdown.entries()).map(([cat, d]) => ({
      category: cat,
      label: d.label,
      itemCount: d.count,
      totalQty: d.qty,
      totalAmount: d.amount,
    })),
    costSummary,
    priceGrade: grade,
    estimateType,
    warnings,
  };
}

// ══════════════════════════════════════════════
// 8. 견적서 Excel 출력용 데이터 생성
// ══════════════════════════════════════════════

export function estimateToExcelData(estimate: FurnitureEstimate): any[][] {
  const rows: any[][] = [];
  
  // 헤더
  rows.push(['제작가구 전문견적서']);
  rows.push(['디자인인사이트(주)', '', '', '', '', '', '연락처: 02-2603-7252']);
  rows.push(['www.desinsight.com']);
  rows.push([]);
  rows.push([`현장명: ${estimate.projectName}`]);
  rows.push([]);
  rows.push([
    `일 금: ${amountToKorean(estimate.grandTotal)}`,
    '', '', '', '', '', '',
    estimate.grandTotal.toLocaleString(), '원', '(부가세포함)',
  ]);
  rows.push([]);
  rows.push(['NO', '제품명', '품명', '코드', 'W', 'D', 'H', '수량', '단가', '금액', '비고']);
  
  // 아이템
  for (const item of estimate.items) {
    rows.push([
      item.no,
      item.name,
      '제작가구',
      item.code,
      item.w,
      item.d,
      item.h,
      item.qty,
      item.unitPrice,
      item.totalPrice,
      item.location,
    ]);
  }
  
  // 소계
  rows.push([]);
  rows.push(['', '', '', '', '', '', '', 'SUB TOTAL', '', estimate.subtotal]);
  rows.push([]);
  rows.push(['', '', '', '', '', '', '', '공급가액', '', estimate.subtotal]);
  rows.push(['', '', '', '', '', '', '', '부가가치세', '', estimate.vat]);
  rows.push(['', '', '', '', '', '', '', '합계금액', '', estimate.grandTotal]);
  
  return rows;
}

// ══════════════════════════════════════════════
// 9. 동연디자인 카탈로그 레퍼런스 (1,816건)
// ══════════════════════════════════════════════

/** 카탈로그 3종 (동연디자인 VOL.21 + 교육용 2024 + 우수제품 2024) 사이즈 레퍼런스 */
export const DONGYEON_REFERENCE: Record<string, {
  count: number;
  w: { min: number; avg: number; max: number };
  d: { min: number; avg: number; max: number };
  h: { min: number; avg: number; max: number };
  surfaceM2: { min: number; avg: number; max: number };
}> = {
  '의자':       { count: 492, w: { min: 375, avg: 657, max: 2436 }, d: { min: 300, avg: 546, max: 1200 }, h: { min: 442, avg: 873, max: 1801 }, surfaceM2: { min: 1.04, avg: 2.84, max: 14.58 } },
  '테이블':     { count: 388, w: { min: 400, avg: 1402, max: 4800 }, d: { min: 400, avg: 700, max: 1200 }, h: { min: 310, avg: 719, max: 1420 }, surfaceM2: { min: 1.02, avg: 5.21, max: 28.56 } },
  '소파':       { count: 356, w: { min: 380, avg: 1073, max: 2436 }, d: { min: 300, avg: 709, max: 1500 }, h: { min: 200, avg: 747, max: 2101 }, surfaceM2: { min: 0.78, avg: 4.35, max: 16.2 } },
  '사무용책상': { count: 158, w: { min: 200, avg: 1199, max: 2200 }, d: { min: 170, avg: 734, max: 1215 }, h: { min: 120, avg: 837, max: 2100 }, surfaceM2: { min: 0.33, avg: 5.01, max: 13.09 } },
  '수납장':     { count: 154, w: { min: 200, avg: 1034, max: 7400 }, d: { min: 350, avg: 620, max: 2190 }, h: { min: 202, avg: 1514, max: 2400 }, surfaceM2: { min: 0.96, avg: 6.27, max: 43.36 } },
  '사물함':     { count: 115, w: { min: 400, avg: 1231, max: 7400 }, d: { min: 300, avg: 395, max: 400 }, h: { min: 400, avg: 1238, max: 2400 }, surfaceM2: { min: 1.84, avg: 5.10, max: 43.36 } },
  '책상':       { count: 61,  w: { min: 200, avg: 1168, max: 2400 }, d: { min: 300, avg: 720, max: 1215 }, h: { min: 411, avg: 764, max: 1161 }, surfaceM2: { min: 1.30, avg: 4.92, max: 13.09 } },
  '책장서가':   { count: 50,  w: { min: 430, avg: 1314, max: 7400 }, d: { min: 300, avg: 404, max: 600 }, h: { min: 400, avg: 1468, max: 2100 }, surfaceM2: { min: 1.75, avg: 4.98, max: 12.16 } },
  '파티션':     { count: 22,  w: { min: 400, avg: 1416, max: 2057 }, d: { min: 300, avg: 768, max: 1146 }, h: { min: 440, avg: 849, max: 1134 }, surfaceM2: { min: 1.30, avg: 6.12, max: 11.98 } },
  '학생용가구': { count: 20,  w: { min: 400, avg: 1080, max: 1800 }, d: { min: 600, avg: 660, max: 900 }, h: { min: 450, avg: 1024, max: 1950 }, surfaceM2: { min: 2.08, avg: 4.89, max: 7.56 } },
};

/** 카테고리 → 동연 레퍼런스 키 매핑 */
const CAT_TO_REF: Record<string, string> = {
  bookshelf_wall: '책장서가', bookshelf_display: '책장서가',
  desk: '사무용책상', table: '테이블', cabinet: '수납장',
  reading_nook: '소파', louver: '파티션', planter: '수납장',
  cube_shelf: '책장서가', moving_wall: '파티션',
};

/** 가구 사이즈가 동연 레퍼런스 범위 내인지 검증 */
export function validateFurnitureSize(w: number, d: number, h: number, category: string): {
  valid: boolean; warnings: string[]; confidence: 'high' | 'medium' | 'low'; extrapolationMultiplier: number;
} {
  const refKey = CAT_TO_REF[category];
  const ref = refKey ? DONGYEON_REFERENCE[refKey] : null;
  if (!ref) return { valid: true, warnings: [], confidence: 'medium', extrapolationMultiplier: 1.0 };

  const warnings: string[] = [];
  const surface = calcSurfaceArea(w, d, h);
  let multiplier = 1.0;

  if (surface < ref.surfaceM2.min * 0.5) {
    warnings.push(`겉넓이 ${surface}㎡ < 레퍼런스 최소 ${ref.surfaceM2.min}㎡의 50% — 치수 확인 필요`);
  }
  if (surface > ref.surfaceM2.max * 1.5) {
    warnings.push(`겉넓이 ${surface}㎡ > 레퍼런스 최대 ${ref.surfaceM2.max}㎡의 150% — 과대 사이즈 주의 (단가 15% 가산)`);
    multiplier = Math.max(multiplier, 1.15);
  }
  if (w > ref.w.max * 1.3) {
    warnings.push(`폭 ${w}mm > 레퍼런스 최대 ${ref.w.max}mm — 특주 사이즈 (단가 15% 가산)`);
    multiplier = Math.max(multiplier, 1.15);
  }

  const confidence = warnings.length === 0 ? 'high' : warnings.length === 1 ? 'medium' : 'low';
  return { valid: warnings.length === 0, warnings, confidence, extrapolationMultiplier: multiplier };
}

/** 금액 → 한글 표기 */
function amountToKorean(amount: number): string {
  const units = ['', '만', '억', '조'];
  const digits = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
  const subUnits = ['', '십', '백', '천'];
  
  if (amount === 0) return '영원정';
  
  const man = Math.floor(amount / 10000);
  const rest = amount % 10000;
  
  let result = '';
  const groups: number[] = [];
  let n = man;
  while (n > 0) {
    groups.push(n % 10000);
    n = Math.floor(n / 10000);
  }
  
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i];
    if (g === 0) continue;
    let gStr = '';
    for (let j = 3; j >= 0; j--) {
      const d = Math.floor(g / Math.pow(10, j)) % 10;
      if (d === 0) continue;
      gStr += (d === 1 && j > 0 ? '' : digits[d]) + subUnits[j];
    }
    result += gStr + units[i + 1];
  }
  
  return result + '원정';
}

// ══════════════════════════════════════════════
// 10. 단독/포함공사 배분율 (cost_taxonomy 기반)
// ══════════════════════════════════════════════

export function getRatioByFu(fuCode: string, mode: 'standalone' | 'included' = 'standalone'): { material: number, labor: number, overhead: number, admin: number, profit: number } {
  const categories = (costTaxonomy as any).categories;
  let matchedCat = null;
  // 찾아보기
  for (const key in categories) {
    if (categories[key].fu_codes && categories[key].fu_codes.includes(fuCode)) {
      matchedCat = categories[key];
      break;
    }
  }
  // 찾았으면 리턴
  if (matchedCat) {
    return mode === 'standalone' ? matchedCat.standalone_ratio : matchedCat.included_ratio;
  }
  // 기본 폴백 (수납장 기준)
  return mode === 'standalone' 
    ? { material: 0.5, labor: 0.25, overhead: 0.09, admin: 0.06, profit: 0.10 }
    : { material: 0.5, labor: 0.368, overhead: 0.132, admin: 0, profit: 0 };
}

export function getRatio(fu: string, mode: 'standalone' | 'included' = 'standalone') {
  return getRatioByFu(fu, mode);
}
